import Avatar from "@mui/material/Avatar"
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined"

export const USER_AVATAR_COLOR = "#224375"

/** Id the automation jobs (e.g. change detection) write as `userId` on history entries. */
const AUTOMATION_USER_ID = "automation"

export function getUserInitials(name: string | null | undefined, email: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (email ?? "").slice(0, 2).toUpperCase()
}

type UserAvatarProps = { name: string | null | undefined; email: string | null | undefined; size: number }

/** Round avatar with the user's initials - the same one the header's user menu shows. */
export function UserAvatar({ name, email, size }: UserAvatarProps) {
  return (
    <Avatar
      sx={{ bgcolor: USER_AVATAR_COLOR, color: "#fff", width: size, height: size, fontSize: Math.round(size * 0.41), fontWeight: 700 }}>
      {getUserInitials(name, email)}
    </Avatar>
  )
}

/** A readable name from a bare `userId`: an email's local part split on `.`/`_`/`-` and capitalized
 * ("pedro.longle@odynx.com" -> "Pedro Longle"); anything else is capitalized as-is. History rows
 * only carry the id, so this is the best name available there. */
export function displayNameFromUserId(userId: string | null | undefined): string {
  const id = (userId ?? "").trim()
  if (!id) return ""
  const base = id.includes("@") ? id.split("@")[0]! : id
  return base
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

type UserIdAvatarProps = { userId: string | null | undefined; size: number }

/**
 * Avatar for a bare `userId` (what history rows carry), always in the user menu's color: the
 * automation user gets a robot icon, anyone else their initials (from the name derived from the id).
 */
export function UserIdAvatar({ userId, size }: UserIdAvatarProps) {
  const id = (userId ?? "").trim()
  if (id.toLowerCase() === AUTOMATION_USER_ID) {
    return (
      <Avatar sx={{ bgcolor: USER_AVATAR_COLOR, color: "#fff", width: size, height: size }}>
        <SmartToyOutlinedIcon sx={{ fontSize: Math.round(size * 0.6) }} />
      </Avatar>
    )
  }
  return <UserAvatar name={displayNameFromUserId(id)} email={id} size={size} />
}
